import { useEffect, useMemo, useState } from "react";
import { BarChart3, Globe, MessageCircle, DollarSign, ShoppingCart, Receipt } from "lucide-react";
import { toast } from "sonner";
import api, { brl } from "./api";

const periods = [
  { id: "hoje", label: "Hoje" },
  { id: "7d", label: "Últimos 7 dias" },
  { id: "30d", label: "Últimos 30 dias" },
  { id: "mes", label: "Este mês" },
  { id: "mes_anterior", label: "Mês anterior" },
  { id: "custom", label: "Personalizado" },
];

const isoDay = (d) => d.toISOString().slice(0, 10);

const rangeFor = (id, custom) => {
  const now = new Date();
  if (id === "hoje") return [isoDay(now), isoDay(now)];
  if (id === "7d") return [isoDay(new Date(now - 6 * 864e5)), isoDay(now)];
  if (id === "30d") return [isoDay(new Date(now - 29 * 864e5)), isoDay(now)];
  if (id === "mes") return [isoDay(new Date(now.getFullYear(), now.getMonth(), 1)), isoDay(now)];
  if (id === "mes_anterior") {
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    return [isoDay(first), isoDay(last)];
  }
  return [custom.start, custom.end];
};

const Card = ({ icon: Icon, label, value }) => (
  <div className="bg-white border border-military/10 rounded-lg p-5">
    <Icon size={18} className="text-military mb-3" />
    <p className="font-serif text-2xl md:text-3xl text-military-dark">{value}</p>
    <p className="text-[10px] uppercase tracking-[0.2em] text-military-dark/50 font-semibold mt-1">{label}</p>
  </div>
);

export default function Reports() {
  const [period, setPeriod] = useState("30d");
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [custom, setCustom] = useState({ start: isoDay(new Date(Date.now() - 29 * 864e5)), end: isoDay(new Date()) });
  const [data, setData] = useState(null);

  useEffect(() => {
    if (period === "custom" && (!custom.start || !custom.end)) return;
    const [start, end] = rangeFor(period, custom);
    api.get(`/admin/reports?start=${start}&end=${end}`).then((r) => setData(r.data)).catch(() => {});
  }, [period, custom]);

  const maxDay = useMemo(() => Math.max(1, ...(data?.by_day || []).map((d) => d.revenue)), [data]);
  const maxProduct = useMemo(() => Math.max(1, ...(data?.top_products || []).map((p) => p.qty)), [data]);

  const summaryText = () => {
    const [start, end] = rangeFor(period, custom);
    const totalUnits = (data?.top_products || []).reduce((s, p) => s + p.qty, 0);
    const top = (data?.top_products || []).slice(0, 3).map((p, i) => `${i + 1}. ${p.name} — ${p.qty} un (${brl(p.revenue)})`).join("\n");
    const fmt = (d) => d.split("-").reverse().join("/");
    return `*Resumo de vendas — leve essenza*\nPeríodo: ${fmt(start)} a ${fmt(end)}\n\nFaturamento total: ${brl(data.total_revenue)}\nPedidos: ${data.orders_count}\nTicket médio: ${brl(data.avg_ticket)}\nProdutos vendidos: ${totalUnits} un\n\n*Mais vendidos:*\n${top || "—"}\n\n*Site:* ${data.by_origin.site?.count || 0} pedidos — ${brl(data.by_origin.site?.revenue || 0)}\n*WhatsApp:* ${data.by_origin.whatsapp?.count || 0} vendas — ${brl(data.by_origin.whatsapp?.revenue || 0)}\n\nPagos: ${data.by_status.pago || 0} · Pendentes: ${data.by_status.pendente || 0} · Cancelados: ${data.by_status.cancelado || 0}`;
  };

  return (
    <div data-testid="admin-reports">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <h1 className="font-serif text-3xl md:text-4xl text-military-dark">Relatórios de vendas</h1>
        <button
          data-testid="generate-summary-button"
          onClick={() => data && setSummaryOpen(true)}
          className="rounded-full bg-military-dark text-offwhite px-5 py-2.5 text-xs uppercase tracking-widest font-semibold hover:bg-military transition-colors"
        >
          Gerar resumo p/ WhatsApp
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="report-periods">
        {periods.map((p) => (
          <button
            key={p.id}
            data-testid={`period-${p.id}`}
            onClick={() => setPeriod(p.id)}
            className={`rounded-full px-4 py-2 text-xs uppercase tracking-widest font-semibold transition-colors ${period === p.id ? "bg-military text-offwhite" : "bg-white border border-military/20 text-military-dark/60 hover:bg-baby"}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {period === "custom" && (
        <div className="flex flex-wrap gap-3 mb-6">
          <input data-testid="custom-start" type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="bg-white border border-military/20 rounded-lg px-3 py-2 text-sm" />
          <input data-testid="custom-end" type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="bg-white border border-military/20 rounded-lg px-3 py-2 text-sm" />
        </div>
      )}

      {!data ? (
        <p className="text-military-dark/50 text-sm">Carregando...</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <Card icon={DollarSign} label="Faturamento" value={brl(data.total_revenue)} />
            <Card icon={ShoppingCart} label="Pedidos" value={data.orders_count} />
            <Card icon={Receipt} label="Ticket médio" value={brl(data.avg_ticket)} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="bg-white border border-military/10 rounded-lg p-5" data-testid="origin-site">
              <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-military-dark/60 mb-2"><Globe size={14} /> Site</p>
              <p className="font-serif text-2xl text-military-dark">{brl(data.by_origin.site?.revenue || 0)}</p>
              <p className="text-xs text-military-dark/50 mt-1">{data.by_origin.site?.count || 0} pedidos</p>
            </div>
            <div className="bg-white border border-military/10 rounded-lg p-5" data-testid="origin-whatsapp">
              <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-military-dark/60 mb-2"><MessageCircle size={14} /> WhatsApp</p>
              <p className="font-serif text-2xl text-military-dark">{brl(data.by_origin.whatsapp?.revenue || 0)}</p>
              <p className="text-xs text-military-dark/50 mt-1">{data.by_origin.whatsapp?.count || 0} vendas registradas</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mb-10" data-testid="status-summary">
            {[["pago", "Pagos", "bg-military/10 text-military"], ["pendente", "Pendentes", "bg-amber-100 text-amber-700"], ["cancelado", "Cancelados", "bg-red-100 text-red-700"]].map(([k, label, cls]) => (
              <span key={k} data-testid={`status-count-${k}`} className={`text-xs uppercase tracking-wider font-semibold px-4 py-2 rounded-full ${cls}`}>
                {label}: {data.by_status[k] || 0}
              </span>
            ))}
          </div>

          <div className="bg-white border border-military/10 rounded-lg p-6 mb-8">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-military-dark/60 mb-6 flex items-center gap-2"><BarChart3 size={14} /> Vendas por dia</p>
            {data.by_day.length === 0 && <p className="text-sm text-military-dark/40">Sem vendas no período.</p>}
            <div className="space-y-2">
              {data.by_day.map((d) => (
                <div key={d.date} className="flex items-center gap-3">
                  <span className="text-xs text-military-dark/50 w-24 shrink-0">{d.date.split("-").reverse().join("/")}</span>
                  <div className="flex-1 h-5 bg-offwhite rounded-full overflow-hidden">
                    <div className="h-full bg-military rounded-full transition-all duration-700" style={{ width: `${(d.revenue / maxDay) * 100}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-military-dark w-24 text-right shrink-0">{brl(d.revenue)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-military/10 rounded-lg p-6">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-military-dark/60 mb-6">Produtos mais vendidos</p>
            {data.top_products.length === 0 && <p className="text-sm text-military-dark/40">Sem vendas no período.</p>}
            <div className="space-y-3">
              {data.top_products.map((p) => (
                <div key={p.name}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-military-dark">{p.name}</span>
                    <span className="text-military-dark/60">{p.qty} un · {brl(p.revenue)}</span>
                  </div>
                  <div className="h-2.5 bg-offwhite rounded-full overflow-hidden">
                    <div className="h-full bg-military rounded-full transition-all duration-700" style={{ width: `${(p.qty / maxProduct) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {summaryOpen && data && (
        <div className="fixed inset-0 z-50 bg-military-dark/40 backdrop-blur-sm flex items-end md:items-center justify-center" onClick={() => setSummaryOpen(false)}>
          <div data-testid="summary-modal" onClick={(e) => e.stopPropagation()} className="bg-offwhite w-full md:max-w-lg rounded-t-lg md:rounded-lg p-6 md:p-8">
            <h2 className="font-serif text-2xl text-military-dark mb-4">Resumo para WhatsApp</h2>
            <textarea
              data-testid="summary-text"
              readOnly
              rows={14}
              className="w-full bg-white border border-military/20 rounded-lg p-4 text-sm text-military-dark font-mono focus:outline-none"
              value={summaryText()}
            />
            <div className="flex flex-wrap gap-3 mt-4">
              <button
                data-testid="summary-copy"
                onClick={() => { navigator.clipboard.writeText(summaryText()); toast.success("Resumo copiado"); }}
                className="flex-1 rounded-full bg-military text-offwhite py-3 text-xs uppercase tracking-widest font-semibold hover:bg-military-dark transition-colors"
              >
                Copiar texto
              </button>
              <a
                data-testid="summary-whatsapp"
                href={`https://wa.me/?text=${encodeURIComponent(summaryText())}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-center rounded-full border border-military/25 text-military-dark py-3 text-xs uppercase tracking-widest font-semibold hover:bg-baby transition-colors"
              >
                Abrir no WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
